import React from "react";
import * as Mui from "@mui/material";
import { Integration } from "model/integration";
import { Card } from "../card";
import { ViewIntegrationModal } from "../../view-integration-modal";
import * as api from "api/api";
import * as Icons from "react-feather";
import { Tag } from "component/shared/tag";
import { effectiveBackfillStatus } from "../../follow-up-boss-status";

type AgentSync = "off" | "sync_data" | "sync_data_and_create";
type MergeStrategy = "fill_in_blanks" | "fub_wins" | "ihf_wins";

interface Props {
    integration: Integration;
    autoOpenModal?: boolean;
    onAutoOpenHandled?: () => void;
}

interface State {
    connected: boolean | null;
    webhooksMissing: number;
    webhookLastHealthCheckDate: number | null;
    agentsMatched: number | null;
    ihfAgentsNotInFub: number | null;
    fubUsersNotInIhf: number | null;
    leadsMatched: number | null;
    leadsUnmatched: number | null;
    backfillStatus: "not_started" | "running" | "complete" | "failed" | null;
    agentSync: AgentSync;
    mergeStrategy: MergeStrategy;
    apiKeyValid: boolean | null;
    lastWebhookProcessed: number | null;
    lastSyncDate: number | null;
    errorCount24h: number;
    backfillLeadsStatus: string | null;
    backfillEventsStatus: string | null;
    modalOpen: boolean;
    showSyncToast: boolean;
    showSyncCompleteToast: boolean;
}

function getStatusChip(connected: boolean | null, webhooksMissing: number, backfillStatus: "not_started" | "running" | "complete" | "failed" | null, apiKeyValid: boolean | null): React.ReactNode {
    if (connected === null) {
        return <Mui.CircularProgress size={16} />;
    }
    if (!connected) {
        return <Tag label="Not Connected" variant="neutral" />;
    }
    // apiKeyValid is checked live against FUB on every status poll — a revoked key
    // must not keep showing "Connected" until the nightly health check (MAX-4007)
    if (webhooksMissing > 0 || apiKeyValid === false) {
        return <Tag label="Connection Lost" variant="error" icon={<Icons.AlertTriangle size={16} />} />;
    }
    if (backfillStatus === "running") {
        return <Tag label="Syncing…" variant="info" icon={<Mui.CircularProgress size={12} color="inherit" />} />;
    }
    if (backfillStatus === "not_started") {
        return <Tag label="Ready to Sync" variant="info" icon={<Icons.Clock size={16} />} />;
    }
    // A failed backfill must not fall through to the green Connected chip (MAX-4043)
    if (backfillStatus === "failed") {
        return <Tag label="Sync Failed" variant="error" icon={<Icons.AlertTriangle size={16} />} />;
    }
    return <Tag label="Connected" variant="success" icon={<Icons.Check size={16} />} />;
}

class Component extends React.Component<Props, State> {

    public constructor(props: Props) {
        super(props);
        this.state = {
            connected: null,
            webhooksMissing: 0,
            webhookLastHealthCheckDate: null,
            agentsMatched: null,
            ihfAgentsNotInFub: null,
            fubUsersNotInIhf: null,
            leadsMatched: null,
            leadsUnmatched: null,
            backfillStatus: null,
            agentSync: (props.integration.data?.followUpBossAgentSyncMode as AgentSync) ?? "off",
            mergeStrategy: (props.integration.data?.followUpBossMergeStrategy as MergeStrategy) ?? "fill_in_blanks",
            apiKeyValid: null,
            lastWebhookProcessed: null,
            lastSyncDate: null,
            errorCount24h: 0,
            backfillLeadsStatus: null,
            backfillEventsStatus: null,
            modalOpen: false,
            showSyncToast: false,
            showSyncCompleteToast: false,
        };
    }

    public componentDidUpdate(prevProps: Props) {
        if (!prevProps.autoOpenModal && this.props.autoOpenModal) {
            this.setState({ modalOpen: true });
            if (this.props.onAutoOpenHandled) this.props.onAutoOpenHandled();
        }
    }

    public componentDidMount() {
        this.fetchStatus();
    }

    private async fetchStatus() {
        try {
            const status = await api.getFollowUpBossStatus(this.props.integration.id);
            this.setState({
                connected: status.connected,
                webhooksMissing: status.webhooksMissing ?? 0,
                webhookLastHealthCheckDate: status.webhookLastHealthCheckDate ?? null,
                agentsMatched: status.agentsMatched ?? null,
                ihfAgentsNotInFub: status.ihfAgentsNotInFub ?? null,
                fubUsersNotInIhf: status.fubUsersNotInIhf ?? null,
                leadsMatched: status.leadsMatched ?? null,
                leadsUnmatched: status.leadsUnmatched ?? null,
                backfillStatus: effectiveBackfillStatus(status.backfillStatus ?? null, status.backfillLeadsStatus),
                apiKeyValid: status.apiKeyValid ?? null,
                lastWebhookProcessed: status.lastWebhookProcessed ?? null,
                lastSyncDate: status.lastSyncDate ?? null,
                errorCount24h: status.errorCount24h ?? 0,
                backfillLeadsStatus: status.backfillLeadsStatus ?? null,
                backfillEventsStatus: status.backfillEventsStatus ?? null,
            });
        } catch(_e) {
            this.setState({ connected: false });
        }
    }

    public render() {
        const { integration } = this.props;
        const { connected, webhooksMissing, webhookLastHealthCheckDate, agentsMatched,
                ihfAgentsNotInFub, fubUsersNotInIhf, leadsMatched, leadsUnmatched,
                backfillStatus, agentSync, mergeStrategy,
                apiKeyValid, lastWebhookProcessed, lastSyncDate, errorCount24h,
                backfillLeadsStatus, backfillEventsStatus, modalOpen,
                showSyncToast, showSyncCompleteToast } = this.state;

        const statusChip = getStatusChip(connected, webhooksMissing, backfillStatus, apiKeyValid);

        const handleStatusChange = (patch: any) => {
            if (patch.backfillStatus === "running" && this.state.backfillStatus !== "running") {
                this.setState({ showSyncToast: true });
            }
            if (patch.backfillStatus === "complete" && this.state.backfillStatus === "running") {
                this.setState({ showSyncCompleteToast: true });
            }
            this.setState(patch);
        };

        return (
            <>
                <Card
                    integration={integration}
                    deletable={connected === false}
                    content={
                        <Mui.Box display="flex" alignItems="center" style={{ minHeight: 32 }}>
                            {statusChip}
                        </Mui.Box>
                    }
                    actions={
                        <Mui.Button
                            variant="text"
                            size="small"
                            color="primary"
                            onClick={() => this.setState({ modalOpen: true })}
                        >
                            View Details <Icons.ChevronRight size={16} style={{ marginLeft: 4 }} />
                        </Mui.Button>
                    }
                />
                <ViewIntegrationModal
                    open={modalOpen}
                    onClose={() => {
                        this.setState({ modalOpen: false });
                        this.fetchStatus();
                    }}
                    integration={integration}
                    connected={connected}
                    webhooksMissing={webhooksMissing}
                    webhookLastHealthCheckDate={webhookLastHealthCheckDate}
                    agentsMatched={agentsMatched}
                    ihfAgentsNotInFub={ihfAgentsNotInFub}
                    fubUsersNotInIhf={fubUsersNotInIhf}
                    leadsMatched={leadsMatched}
                    leadsUnmatched={leadsUnmatched}
                    backfillStatus={backfillStatus}
                    agentSync={agentSync}
                    mergeStrategy={mergeStrategy}
                    apiKeyValid={apiKeyValid}
                    lastWebhookProcessed={lastWebhookProcessed}
                    lastSyncDate={lastSyncDate}
                    errorCount24h={errorCount24h}
                    backfillLeadsStatus={backfillLeadsStatus}
                    backfillEventsStatus={backfillEventsStatus}
                    onStatusChange={handleStatusChange}
                />
                {/* Sync started toast */}
                <Mui.Snackbar
                    open={showSyncToast}
                    autoHideDuration={6000}
                    onClose={() => this.setState({ showSyncToast: false })}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                >
                    <Mui.Paper elevation={3} style={{ padding: "12px 16px", backgroundColor: "#e8f5e9", maxWidth: 320 }}>
                        <Mui.Box display="flex" alignItems="flex-start" justifyContent="space-between">
                            <Mui.Box display="flex" alignItems="flex-start" style={{ gap: 8 }}>
                                <Icons.CheckCircle size={18} style={{ color: "#2e7d32", marginTop: 2, flexShrink: 0 }} />
                                <Mui.Box>
                                    <Mui.Typography variant="body2" style={{ fontWeight: 600, color: "#1b5e20" }}>Sync started!</Mui.Typography>
                                    <Mui.Typography variant="body2" style={{ color: "#2e7d32" }}>
                                        Your data is syncing in the background. This may take some time for larger record sets.
                                    </Mui.Typography>
                                </Mui.Box>
                            </Mui.Box>
                            <Mui.IconButton size="small" onClick={() => this.setState({ showSyncToast: false })} style={{ marginLeft: 8 }}>
                                <Icons.X size={14} />
                            </Mui.IconButton>
                        </Mui.Box>
                    </Mui.Paper>
                </Mui.Snackbar>
                {/* Sync complete toast */}
                <Mui.Snackbar
                    open={showSyncCompleteToast}
                    autoHideDuration={8000}
                    onClose={() => this.setState({ showSyncCompleteToast: false })}
                    anchorOrigin={{ vertical: "top", horizontal: "center" }}
                >
                    <Mui.Paper elevation={3} style={{ padding: "10px 16px", backgroundColor: "#e8f5e9", maxWidth: 600 }}>
                        <Mui.Box display="flex" alignItems="center" justifyContent="space-between" style={{ gap: 8 }}>
                            <Mui.Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                <Icons.CheckCircle size={16} style={{ color: "#2e7d32", flexShrink: 0 }} />
                                <Mui.Typography variant="body2" style={{ color: "#1b5e20" }}>
                                    Your Follow Up Boss initial sync is complete — leads and activity are now available in Follow Up Boss.
                                </Mui.Typography>
                            </Mui.Box>
                            <Mui.IconButton size="small" onClick={() => this.setState({ showSyncCompleteToast: false })}>
                                <Icons.X size={14} />
                            </Mui.IconButton>
                        </Mui.Box>
                    </Mui.Paper>
                </Mui.Snackbar>
            </>
        );
    }

}

export const FollowUpBossIntegration = Component;
